import styled from 'styled-components';
import {
  DropDown,
  Flex,
  FormField,
  Radio,
  formFieldRequiredLabel as requiredLabel,
} from '@elementary/common-ui/components';
import {
  FormControlLabel as FormControlLabelBase,
  RadioGroup,
  Typography,
} from '@mui/material';
import { Controller, UseFormReturn } from 'react-hook-form';
import { CadenceType, DAYS, HOURS } from 'shared/utils/cronUtils';
import { RuleFormFields } from 'shared/types';

const FormControlLabel = styled(FormControlLabelBase)`
  gap: 8px;

  .MuiFormControlLabel-label {
    font-size: 14px;
  }
`;

const SelectRow = styled(Flex)`
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 8px;
`;

type Props = {
  methods: UseFormReturn<RuleFormFields>;
};

const CadenceFormContent = ({ methods }: Props) => {
  const cadenceType = methods.watch('cadenceType');

  return (
    <FormField title="Digest cadence">
      <Controller
        control={methods.control}
        name="cadenceType"
        rules={{ required: requiredLabel }}
        render={({ field }) => (
          <RadioGroup
            {...field}
            sx={{ gap: 2 }}
            onChange={(_, value) => {
              if (value != null) {
                methods.setValue('cadenceType', value as CadenceType, {
                  shouldValidate: true,
                });
                if (value === 'daily') {
                  methods.setValue('cadenceDay', undefined);
                } else if (value === 'weekly') {
                  if (methods.getValues('cadenceDay') == null) {
                    methods.setValue('cadenceDay', 1);
                  }
                }
              }
            }}
          >
            <FormControlLabel
              sx={{ m: 0 }}
              value="daily"
              control={<Radio sx={{ paddingY: 0, pl: '9px', pr: '2px' }} />}
              label="Daily"
            />
            <FormControlLabel
              sx={{ m: 0 }}
              value="weekly"
              control={<Radio sx={{ paddingY: 0, pl: '9px', pr: '2px' }} />}
              label="Weekly"
            />
          </RadioGroup>
        )}
      />

      <SelectRow>
        {cadenceType === 'weekly' && (
          <>
            <Typography sx={{ fontSize: '14px', color: 'common.gray2' }}>
              Every
            </Typography>
            <Controller
              control={methods.control}
              name="cadenceDay"
              rules={{
                required: cadenceType === 'weekly' ? requiredLabel : false,
              }}
              render={({ field }) => (
                <DropDown
                  value={String(field.value ?? 1)}
                  onChange={(val) => field.onChange(Number(val))}
                  options={DAYS.map((d) => ({
                    value: String(d.value),
                    label: d.label,
                  }))}
                  showClear={false}
                  sortOptions={false}
                  sx={{ minWidth: 140 }}
                />
              )}
            />
            <Typography sx={{ fontSize: '14px', color: 'common.gray2' }}>
              at
            </Typography>
          </>
        )}

        {cadenceType === 'daily' && (
          <Typography sx={{ fontSize: '14px', color: 'common.gray2' }}>
            At
          </Typography>
        )}

        <Controller
          control={methods.control}
          name="cadenceHour"
          rules={{ required: requiredLabel }}
          render={({ field }) => (
            <DropDown
              value={String(field.value ?? 9)}
              onChange={(val) => field.onChange(Number(val))}
              options={HOURS.map((h) => ({
                value: String(h.value),
                label: h.label,
              }))}
              showClear={false}
              sortOptions={false}
              sx={{ minWidth: 120 }}
            />
          )}
        />
      </SelectRow>
    </FormField>
  );
};

export default CadenceFormContent;
